import {
  DocumentReference,
  DocumentSnapshot,
  FieldValue,
} from "@atgof-firebase/firebase";
import Handlers from "./handlers";
import { authHeadersForCurrentUser } from "../auth";
import { UPLOAD_STATUS_HEADER } from "../../service-worker/common";
import { getDocumentReference } from "../../common/firestore";
import {
  APP_VERSION_HEADER,
  SERVICE_WORKER_ERROR_HEADER,
  UPLOAD_DOC_REF_HEADER,
} from "@atgof-common/uploads";
import { nativeApplicationVersion } from "expo-application";

type Primitive = string | boolean | number | null;

export type UploadMetadata = Record<
  string,
  Primitive | Record<string, Primitive> | Array<Primitive>
>;

type UploadError = {
  name: string;
  message: string;
  stack?: string;
};

export type UploadStatus = {
  docRefPath: string;
  storagePath: string;
  mimeType?: string | null;
  originalExt?: string | null;
  cacheKey?: string;
  error?: UploadError;
};

export function uploadError(error: unknown): UploadError {
  let name = "Unknown error";
  let message: string | undefined;
  let stack: string | undefined;
  if (typeof error === "object" && error) {
    if ("name" in error && typeof error.name === "string") name = error.name;
    if ("message" in error && typeof error.message === "string")
      message = error.message;
    if ("stack" in error && typeof error.stack === "string")
      stack = error.stack;
  }
  if (!message) message = `${error}`;
  return { message, name, stack };
}

function mimeTypeFromDataURI(uri: string | undefined) {
  const m = uri?.match(/^data:([^;,]+);/);
  return m ? m[1] : null;
}

export function fileExtensionForURI(uri: string) {
  const m = uri.match(/^file:.*(\.[^\.]+)$/);
  return m && m[1];
}

export abstract class Uploader {
  protected readonly endpointURI: string;
  protected handlers = new Handlers<UploadStatus[]>("upload");
  private completedStoragePaths = new Set<string>();
  private pendingStoragePaths = new Set<string>();

  public constructor(endpointURI: string) {
    this.endpointURI = endpointURI;
  }

  public abstract onUserConnected(user: DocumentSnapshot): Promise<void>;

  public onUploads(handler: (uploads: UploadStatus[]) => void) {
    return this.handlers.add(handler, this.getUploadStatuses());
  }

  public async uploadFile(
    input: string | File | null,
    storagePath: string,
    docRef: DocumentReference | undefined,
    user: DocumentSnapshot,
    manuallyInvoked: boolean,
    metadata: UploadMetadata | undefined = undefined,
  ) {
    if (
      this.completedStoragePaths.has(storagePath) ||
      this.pendingStoragePaths.has(storagePath) ||
      (!manuallyInvoked && this.isUploadingInBackground(storagePath))
    ) {
      return;
    }
    const isRetrying = !input;
    try {
      this.pendingStoragePaths.add(storagePath);
      const localURI = (typeof input === "string" && input) || undefined;
      const mimeType =
        typeof metadata?.mimeType === "string"
          ? metadata.mimeType
          : mimeTypeFromDataURI(localURI);
      const originalExt =
        input && typeof input === "object"
          ? fileExtensionForURI("file:" + input.name)
          : undefined;
      let status = this.getUploadStatus(storagePath);
      if (!status) {
        if (!docRef) {
          console.error(`Unable to retry file upload for ${storagePath}`);
          return;
        }
        if (!isRetrying) {
          docRef.set({
            ...(metadata ?? {}),
            path: storagePath,
            createdBy: user?.ref,
            createdAt: FieldValue.serverTimestamp(),
            lastModifiedBy: user?.ref,
            lastModifiedAt: FieldValue.serverTimestamp(),
            uploadCompletedAt: false,
            ...(mimeType ? { mimeType } : {}),
          });
        }
        status = {
          docRefPath: docRef.path,
          storagePath,
          mimeType,
          originalExt,
        };
      } else delete status.error;
      await this.setStatus(storagePath, status);
      let cacheKey;
      if (input) {
        try {
          cacheKey = await this.copyTemporaryContentToCache(storagePath, input);
          await this.setStatus(storagePath, {
            ...status,
            cacheKey,
          });
        } catch (error) {
          console.error(`Unable to cache local URI for ${storagePath}`, error);
        }
      } else {
        cacheKey = status.cacheKey ?? this.cacheKeyForStoragePath(storagePath);
      }
      if (!cacheKey) throw new Error(`No file to upload for ${storagePath}`);
      status.cacheKey = cacheKey;
      await this.uploadFromLocalFile(status);
      this.completedStoragePaths.add(storagePath);
      docRef?.update({ uploadCompletedAt: FieldValue.serverTimestamp() });
      this.setFinished(storagePath);
    } catch (error) {
      console.error(`Error uploading file ${storagePath}, ${error}`);
      this.setStatus(storagePath, (status) => ({
        ...status,
        error: uploadError(error),
      }));
    } finally {
      this.pendingStoragePaths.delete(storagePath);
    }
  }

  public cancelUpload(docRef: DocumentReference) {
    // TODO Either implement proper cancellation, or remove this
    // function after re-factoring its callers
    const status = this.getUploadStatuses().find(
      ({ docRefPath }) => docRefPath === docRef.path,
    );
    if (status) this.setFinished(status.storagePath);
  }

  protected abstract getUploadStatuses(): UploadStatus[];
  protected abstract getUploadStatus(
    storagePath: string,
  ): UploadStatus | undefined;
  protected abstract setUploadStatus(
    storagePath: string,
    status: UploadStatus,
  ): void;
  protected abstract setFinished(storagePath: string): Promise<void>;

  protected abstract copyTemporaryContentToCache(
    storagePath: string,
    tempFileURIOrContent: string | File,
  ): Promise<string | undefined>;
  protected abstract cacheKeyForStoragePath(
    storagePath: string,
  ): string | undefined;
  public abstract getUriForStoragePath(
    storagePath: string,
  ): Promise<string | undefined>;

  public abstract uploadFromLocalFile(upload: UploadStatus): Promise<Response>;
  protected abstract isUploadingInBackground(storagePath: string): boolean;

  public async retryUploads(
    user: DocumentSnapshot,
    uploads: UploadStatus[],
    manuallyInvoked: boolean,
  ) {
    for (const upload of uploads) {
      const { storagePath, docRefPath } = upload;
      let docRef;
      try {
        docRef = getDocumentReference(user, docRefPath);
      } catch (err) {
        console.warn(`Unable to get ref for doc ${docRefPath}`, err);
      }
      try {
        await this.uploadFile(null, storagePath, docRef, user, manuallyInvoked);
      } catch (err) {
        console.warn(`Unable to upload ${storagePath}`, err);
      }
    }
  }

  protected handleUpdate(uploads: UploadStatus[]) {
    this.handlers.handle(uploads); // TODO Sort uploads consistently
  }

  protected shouldIncludeStatusHeader() {
    return false;
  }

  protected async uploadForm(
    upload: UploadStatus,
    form: FormData,
    fromServiceWorker: boolean = false,
  ) {
    form.set("doc", upload.docRefPath);
    const { originalExt } = upload;
    if (originalExt) form.append("originalExt", originalExt);
    const headers = new Headers();
    const authHeaders = await authHeadersForCurrentUser();
    for (const k of Object.keys(authHeaders)) {
      headers.set(k, authHeaders[k]);
    }
    if (this.shouldIncludeStatusHeader()) {
      headers.set(UPLOAD_STATUS_HEADER, btoa(JSON.stringify(upload)));
    }
    headers.set(
      APP_VERSION_HEADER,
      (nativeApplicationVersion ??
        process.env.EXPO_PUBLIC_APP_VERSION ??
        "unknown") + (fromServiceWorker ? " sw" : ""),
    );
    headers.set(UPLOAD_DOC_REF_HEADER, upload.docRefPath);
    const request = new Request(this.endpointURI, {
      method: "POST",
      body: form,
      headers,
    });
    const response = await fetch(request);
    if (response.status !== 201 && !fromServiceWorker) {
      throw new Error(
        (response.headers.has(SERVICE_WORKER_ERROR_HEADER)
          ? "Service worker"
          : "Server") + ` returned status ${response.status}`,
      );
    }
    return response;
  }

  protected async setStatus(
    storagePath: string,
    status: UploadStatus | ((existingStatus: UploadStatus) => UploadStatus),
  ) {
    if (typeof status === "function") {
      const existingStatus = this.getUploadStatus(storagePath);
      if (existingStatus)
        this.setUploadStatus(storagePath, status(existingStatus));
    } else this.setUploadStatus(storagePath, status);
  }
}
